import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, ArrowLeft, Sparkles } from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { SALES_AGENT_BRIEFINGS_URL, SALES_AGENT_BRIEFING_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import BriefingView from "./BriefingView";
import { ScopePicker, EmptyState } from "./shared";

/** Sales Manager Intelligence -> Briefings: every daily briefing, newest first. */
export default function BriefingHistory() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [scope, setScope] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        fetch(SALES_AGENT_BRIEFINGS_URL, scope ? { scope, page } : { page })
            .then(res => {
                if (res?.status === "success") setData(res.data);
                else ToastMsg("error", res?.message || "Could not load briefings");
            })
            .finally(() => setLoading(false));
    }, [scope, page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const show = (id) => fetch(SALES_AGENT_BRIEFING_URL(id)).then(res => {
        if (res?.status === "success") setOpen(res.data);
        else ToastMsg("error", res?.message || "Could not open the briefing");
    });

    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    return (
        <ContainerCard>
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Back" onClick={() => open ? setOpen(null) : navigate("/sales-agent")}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                            <History className="h-4 w-4 text-primary" /> Briefing history
                        </h1>
                    </div>
                    {!open && <ScopePicker scopes={data?.scopes} value={scope || data?.scope?.key} onChange={v => { setPage(1); setScope(v); }} />}
                </div>

                {open ? (
                    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                        <p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">{open.scope_label}</p>
                        <BriefingView run={open} />
                        {open.ai_used && (
                            <p className="mt-3 text-[11px] text-gray-400">
                                Model {open.model} · prompt {open.prompt_version} · {open.ai_seconds}s · USD {open.ai_cost_usd}
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {!data && loading && <div className="space-y-2">{[0, 1, 2].map(i => <Skeleton key={i} className="h-14" />)}</div>}
                        {data && data.results.length === 0 && (
                            <EmptyState icon={History} title="No briefings yet">Briefings appear here once the first one is written.</EmptyState>
                        )}
                        {data && data.results.length > 0 && (
                            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                                {data.results.map(r => (
                                    <li key={r.id}>
                                        <button type="button" onClick={() => show(r.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800">
                                            <div className="w-24 shrink-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">{dayjs(r.period_date).format("DD MMM")}</p>
                                                <p className="text-[11px] text-gray-400">{dayjs(r.period_date).format("dddd")}</p>
                                            </div>
                                            <p className="min-w-0 flex-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">{r.summary}</p>
                                            <span className={cn("shrink-0 rounded px-1.5 py-0.5 text-[10px]",
                                                r.ai_used ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")}>
                                                {r.ai_used ? <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />AI</span> : "Rules"}
                                            </span>
                                            <span className="w-16 shrink-0 text-right text-[11px] capitalize text-gray-400">{r.trigger}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {data && pages > 1 && (
                            <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                                <span>{page} / {pages}</span>
                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
